import { ref, onMounted, onUnmounted } from 'vue';
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { useTrackerStore } from '@/stores/tracker';
import { useBreakStore } from '@/stores/break';
import { BreakModeEnum } from '@/utils/dataAnalyzers';
import { logDebug, logError, logInfo } from '@/utils/logger';

export interface UseElapsedPlaybackTimeOptions {
  spoofIntervalMs?: number;
  /** Elapsed time stops advancing while this returns true (e.g. a detail drawer is open). */
  isPaused?: () => boolean;
}

/**
 * Tracks elapsed playback seconds via the real SDK position updates when
 * available, falling back to a spoof timer for local dev — the isRealEnv/
 * SDK-vs-spoof plumbing shared by useOverlayTimeline and several overlays.
 */
export function useElapsedPlaybackTime(options: UseElapsedPlaybackTimeOptions = {}) {
  const { spoofIntervalMs = 500, isPaused = () => false } = options;

  const elapsedSeconds = ref(0);
  let isRealEnv = false;
  let spoofInterval: ReturnType<typeof setInterval> | null = null;
  let thisInstanceCallback: ((positionMs: number) => void) | null = null;

  onMounted(() => {
    const storeTracker = useTrackerStore();
    const storeBreak = useBreakStore();

    let isRealEnvBySdk = false;
    try {
      isRealEnvBySdk = CommunicationUtil.isContentOverlay();
    } catch (error) {
      logError('useElapsedPlaybackTime — isContentOverlay() failed, falling back:', error);
    }

    isRealEnv =
      isRealEnvBySdk ||
      (!!storeTracker.isIframe &&
        !([BreakModeEnum.FULLMODE, BreakModeEnum.BROWSER] as (string | null)[]).includes(
          storeBreak.breakDisplayMode,
        ));

    if (isRealEnv) {
      const onPosition = (positionMs: number) => {
        if (!isPaused()) elapsedSeconds.value = positionMs / 1000;
      };
      thisInstanceCallback = onPosition;
      CommunicationUtil.onUpdateCurrentPosition = onPosition;
      logInfo('useElapsedPlaybackTime — listening to real SDK position updates');
    } else {
      logDebug('useElapsedPlaybackTime — using spoof timer for local testing');
      spoofInterval = setInterval(() => {
        if (!isPaused()) elapsedSeconds.value += spoofIntervalMs / 1000;
      }, spoofIntervalMs);
    }
  });

  onUnmounted(() => {
    // // Only clear the SDK callback if it's still ours — a later instance may have overwritten it.
    if (isRealEnv && CommunicationUtil.onUpdateCurrentPosition === thisInstanceCallback) {
      CommunicationUtil.onUpdateCurrentPosition = undefined;
    } else if (spoofInterval) {
      clearInterval(spoofInterval);
    }
  });

  return { elapsedSeconds };
}
